/* eslint-disable */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ECharts from 'echarts-for-react';
import Papa from 'papaparse';
import { Box, Tab, Table, TableBody, TableCell, TableContainer, TableRow, Tabs, ToggleButton, ToggleButtonGroup, CircularProgress } from "@mui/material";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip } from '@mui/material';

import { getDiagnosisResultNoiseByDayByQuery, getDiagnosisResultVibrationSensorsGraphDayByQuery, getEquipmentTemperatureGraphDayByQuery } from "components/axios/equipmentAlertsAxios";
import MainCard from "components/MainCard";
import { getBatLevelImgByBatLevel, getDiagnosisGradeColor } from "data/data";
import CSVReaderGraphTab from "./CSVReaderGraphTab";
import EquipmentPartSensorsGraphsLayer from "components/layer/EquipmentPartSensorsGraphsLayer";
import { getDownloadDirectByQuery } from "components/axios/fileManagerAxios";
import { useEnumApi } from "contexts/EnumApiContext";
import useConfig from 'hooks/useConfig';
import { fromServerFormat } from "components/date_picker/dateUtils";
import { buildBoxPlotData, buildNoiseData, buildTemperatureData, buildVibrationData, isSameGraphData } from "../../../utils/graphUtils";
import { updateNoiseChart, updateTempChart, updateVibrationChart } from "utils/chartUpdater";

const DELAY_TIME = Number(process.env.REACT_APP_DELAY_TIME) || 60000;

const chartDataInit = (textColor,bgColor) => ({
    textStyle : {
        color : textColor
    },
    tooltip: {
        trigger: 'axis'
    },
    legend: {
        data : [],
        left : 'center',
        top: 0,
        backgroundColor : bgColor,
        textStyle: {
            color: textColor
        }
    },
    grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
    },
    xAxis: {
        type: 'category',
        boundaryGap: false,
        data: [ ]
    },
    yAxis: {
        type: 'value'
    },
    series: [ ]
});

const TAB = Object.freeze({
    GRAPH: 0,
    TEMPERATURE: 1,
    CSV: 2,
});

function EquipmentPartSensorsGraphsWithBox({equipment, equipmentPartId, sensorPosition, equipmentPartPositionName, type="none"}) {
    const { getEnumValueByEnumNameAndCode } = useEnumApi();
    const [equipmentPartName, setEquipmentPartName] = useState('');
    const { t } = useTranslation();
    
    const { mode } = useConfig();
    const textColor = mode === 'light' ? '#262626' : '#ffffff';
    const bgColor = mode === 'light' ? '#f9f9fa' : '#2f2f2f';

    const [uiReady, setUiReady] = useState({
        vibration: false,
        temperature: false,
        csv: false,
    });

    // initial chart options only, real-time updates via setOption
    const vibrationBaseOptions = useRef(chartDataInit(textColor,bgColor));
    const tempBaseOptions = useRef(chartDataInit(textColor,bgColor));

    const [csvOptions, setCsvOptions] = useState(() => chartDataInit(textColor,bgColor));
    const [file, setFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [remainLife, setRemainLife] = useState(0); 
    const [value, setValue] = useState(TAB.GRAPH);
    const [isVisible, setIsVisible] = useState(
        typeof document !== 'undefined' ? !document.hidden : true
    );
    
    const [vibrationChartType, setVibrationChartType] = useState("AVG");    // AVG, BOX
    const [hasBoxData, setHasBoxData] = useState(false);
    const [hasTempData, setHasTempData] = useState(false);

    // ⭐ [추가] 차트별 개별 로딩 상태 관리
    const [graphLoading, setGraphLoading] = useState(false);
    const [tempLoading, setTempLoading] = useState(false);

    const vibrationPollingRef = useRef(null);
    const tempPollingRef = useRef(null);

    const chartRef = useRef(null); // value===0 전용
    const tempChartRef = useRef(null);

    const vibrationGraphDataRef = useRef(null);
    const prevNoiseDataRef = useRef(null);
    const prevTempDataRef = useRef(null);
    
    const prevCsvHashRef = useRef(null);
    const getCsvHash = (text) => {
        return text.length + ':' + text.slice(0, 100);
    };

    const startPolling = (ref, fn, delay) => {
        stopPolling(ref);            // ⭐ 이 줄 추가 (중복/유령 interval 방지)
        ref.current = setInterval(fn, delay);
    };

    const stopPolling = (ref) => {
        if (!ref.current) return;

        clearInterval(ref.current);
        ref.current = null;
    };

    const clearChart = (chart) => {
        if (!chart) return;
        chart.clear();
    };

    // const vibrationDrawer = {
    //     AVG: updateVibrationChart,
    //     BOX: updateVibrationBoxPlotChart,
    // };

    // const drawCurrentVibrationChart = (chart, data) => {
    //     vibrationDrawer[vibrationChartType](chart, data, t);
    // };

    const updateEquipmentPartName = (list) => {
        if (list.length === 0) return;

        const name = list[0].equipmentPartName;
        setEquipmentPartName(prev => prev || name);
    };

    const enableUI = (type) => {
        setUiReady(prev =>
            prev[type]
                ? prev
                : {
                    ...prev,
                    [type]: true
                }
        );
    };

    const graphConfigs = {
        vibration: {
            request: getDiagnosisResultVibrationSensorsGraphDayByQuery,
            equipmentPartName: (res) => {
                const list = res.averagePage?.content ?? res.rawList ?? [];
                return updateEquipmentPartName(list);
            },
            responseParser: (res) => {
                const averages = res.averagePage?.content ?? [];
                const rawDetails = res.rawList ?? [];

                averages.sort((a, b) => a.dateTimeLabel.localeCompare(b.dateTimeLabel));
                rawDetails.sort((a, b) => a.dateTimeLabel.localeCompare(b.dateTimeLabel));

                return {
                    avg: buildVibrationData(averages),
                    box: buildBoxPlotData(rawDetails)
                };
            },
            prevDataRef: vibrationGraphDataRef,
            enableUI: () => enableUI("vibration"),
            drawer: updateVibrationChart
        },

        noise: {
            request: getDiagnosisResultNoiseByDayByQuery,
            equipmentPartName: (res) => {
                const list = res._embedded?.groupByDailyDiagnosisResultNoises ?? [];
                return updateEquipmentPartName(list);
            },
            responseParser: (res) => {
                const noise = res._embedded?.groupByDailyDiagnosisResultNoises ?? [];
                noise.sort((a, b) => a.ymd.localeCompare(b.ymd));
                return buildNoiseData(noise);
            },
            prevDataRef: prevNoiseDataRef ,
            enableUI: () => enableUI("vibration"),
            drawer: updateNoiseChart
        },

        temperature: {
            request: getEquipmentTemperatureGraphDayByQuery,
            equipmentPartName: (res) => {
                const list = res._embedded?.groupByDailyEquipmentTemperatures ?? [];
                return updateEquipmentPartName(list);
            },
            responseParser: (res) => {
                const temperature = res._embedded?.groupByDailyEquipmentTemperatures ?? [];
                temperature.sort((a, b) => a.ymd.localeCompare(b.ymd));
                return  buildTemperatureData(temperature);
            },
            prevDataRef: prevTempDataRef,
            enableUI: () => enableUI("temperature"),
            drawer: updateTempChart
        }
    };

    // =========================
    // Graph Fetchers
    // =========================
    const fetchGraph = async ({
        chart,
        graphParam,
        config,
        chartType,
        drawChart = true,
        forceDraw = false, // 강제 그리기 플래그 추가
        isPolling = false
    }) => {
        const {
            request,
            equipmentPartName,
            responseParser,
            prevDataRef,
            enableUI,
            drawer
        } = config;

        // ⭐ 핵심: 단순 백그라운드 폴링일 때는 화면 전체를 가리는 로더를 켜지 않습니다.
        if (!isPolling) {
            if (config === graphConfigs.vibration) {
                setGraphLoading(true);
            } else if (config === graphConfigs.temperature) {
                setTempLoading(true);
            }
        }

        try {
            const data = await request(graphParam);
            if (!data) {
                if (chart) clearChart(chart); // 👈 null 안전장치 보강
                return;
            }

            equipmentPartName(data)
            const nextData = responseParser(data);

            // 진동 박스 유무 설정
            if (nextData?.box && nextData.box.x && nextData.box.x.length > 0) {
                setHasBoxData(true);
            } else {
                setHasBoxData(false);
            }

            // 온도 데이터 유무 설정
            if (config === graphConfigs.temperature) {
                if (nextData && nextData.temps && nextData.temps.length > 0) {
                    setHasTempData(true);
                } else {
                    setHasTempData(false);
                }
            }
            
            if (!forceDraw && isSameGraphData(prevDataRef.current, nextData, chartType)) {
                return;
            }

            prevDataRef.current = nextData;
            enableUI();

            // 실시간 그리기 수행
            if (drawChart) {
                if (chart && chart.id) { // 확실하게 전달받은 차트 인스턴스가 존재할 때만 그리기
                    drawer(chart, nextData, t, textColor, bgColor, chartType);
                } else {
                    console.warn("차트 인스턴스가 준비되지 않아 그리기를 보류합니다.");
                }
            }
        } catch (e) {
            console.error(e);
        } finally {
            // 데이터 요청이 끝나면 로더를 꺼줍니다.
            if (config === graphConfigs.vibration|| config === graphConfigs.noise) {
                setGraphLoading(false);
            } else if (config === graphConfigs.temperature) {
                setTempLoading(false);
            }
        }
    };

    useEffect(() => {
        const handleVisibility = () => {
            setIsVisible(!document.hidden);
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, []);
    
    useEffect(()=>{
        setValue(TAB.GRAPH);
    },[sensorPosition]);

    useEffect(() => {
        const chart = chartRef.current?.getEchartsInstance();
        if (chart) {
            chart.setOption(chartDataInit(textColor,bgColor), true);
        }
        const tempChart = tempChartRef.current?.getEchartsInstance();
        if (tempChart) {
            tempChart.setOption(chartDataInit(textColor,bgColor), true);
        }
    }, []);

    useEffect(() => {
        // ⭐ 센서 변경 시 즉시 로더 표시하여 이전 잔재 숨기기
        setGraphLoading(true);
        setTempLoading(true);

        vibrationGraphDataRef.current = null
        prevNoiseDataRef.current = null;
        prevTempDataRef.current = null;
        prevCsvHashRef.current = null;

        setHasBoxData(false); // 기존 진동 박스 유무 초기화
        setHasTempData(false); // 👈 [추가] 기존 온도 유무 상태도 리셋!

        const chart = chartRef.current?.getEchartsInstance();
        if (chart) {
            chart.clear();
            chart.setOption(chartDataInit(textColor,bgColor), true); // ⭐ 핵심
        }

        const tempChart = tempChartRef.current?.getEchartsInstance();
        if (tempChart) {
            tempChart.clear();
            tempChart.setOption(chartDataInit(textColor,bgColor), true); // ⭐ 핵심
        }

    }, [sensorPosition?.id]);

    useEffect(() => {
        if (value === TAB.GRAPH) {
            setGraphLoading(true); // ⭐ 탭 누르자마자 즉시 로딩 실행
            vibrationGraphDataRef.current = null;
            prevNoiseDataRef.current = null;
        }

        if (value === TAB.TEMPERATURE) {  // 온도
            setTempLoading(true);  // ⭐ 탭 누르자마자 즉시 로딩 실행
            prevTempDataRef.current = null;
        }
    }, [value]);

    useEffect(() => {
        setHasBoxData(false);
    }, [sensorPosition?.id]);

    // ==========================================
    // 1. [핵심] 화면 진입 또는 센서 변경 시: 배경에서 즉시 API를 동시 병렬 호출하여 uiReady 결정
    // ==========================================
    useEffect(() => {
        if (!sensorPosition || !isVisible) return;

        // 💡 1-1. 배경에서 진동 or 소음 API 무조건 최초 1회 즉시 호출
        const graphParam = { "sensorPosition.id": sensorPosition.id };
        const preFetchVibration = async () => {
            // let graphConfig;
            if (sensorPosition.sensorType === 'N') {
                // graphConfig = graphConfigs.noise;
                await fetchGraph({
                    chart: null,
                    graphParam,
                    config : graphConfigs.noise,
                    drawChart: false // uiReady 상태만 켜기 위해 false
                });
            } else {
                // graphConfig = vibrationChartType === 'AVG' ? graphConfigs.vibrationAvg : graphConfigs.vibrationBox;
                await fetchGraph({
                    chart: null,
                    graphParam,
                    // graphParam : sensorPosition.id ,
                    config : graphConfigs.vibration,
                    drawChart: false
                });
            }
        };

        // 💡 1-2. 배경에서 온도 API 무조건 최초 1회 즉시 호출 (뷰 권한이 'Y'일 때만)
        const preFetchTemperature = async () => {
            await fetchGraph({
                chart: null,
                graphParam,
                config: graphConfigs.temperature,
                drawChart: false // 일단 uiReady 상태를 켜기 위해 false
            });
        };

        // 마운트 직후 바로 실행하여 탭 노출 여부 결정
        preFetchVibration();
        preFetchTemperature();

    }, [sensorPosition?.id, isVisible]);


    // ==========================================
    // 2. 진동 / 소음 실시간 Polling 및 렌더링 제어
    // ==========================================
    useEffect(() => {
        // 사용자가 진동 탭(GRAPH)을 보고 있을 때만 실시간 Polling 작동
        if (!sensorPosition || !isVisible || value !== TAB.GRAPH) {
            stopPolling(vibrationPollingRef);
            return;
        }

        const graphParam = { "sensorPosition.id": sensorPosition.id };
        const fetchVibrationOrNoise = async (isFirstRun = false) => {
            // setGraphLoading(true); // ⭐ 그리기 시작할 때 로딩 온
            // try {
                const chart = chartRef.current?.getEchartsInstance();
                // ⭐ 방어 코드: 만약 순간적으로 차트 인스턴스를 잡지 못했다면 로더라도 종료해 줍니다.
                if (!chart) {
                    setGraphLoading(false);
                    return;
                }

                // let graphConfig;
                if (sensorPosition.sensorType === 'N') {
                    // graphConfig = graphConfigs.noise;
                    await fetchGraph({
                        chart,
                        graphParam,
                        config : graphConfigs.noise,
                        drawChart: true,
                        forceDraw: isFirstRun,
                        isPolling: !isFirstRun
                    });
                } else {
                    await fetchGraph({
                        chart,
                        graphParam,
                        // graphParam : sensorPosition.id,
                        config : graphConfigs.vibration,
                        drawChart: true,
                        chartType : vibrationChartType,
                        forceDraw: isFirstRun,
                        isPolling: !isFirstRun
                    });
                }
            // } finally {
            //     setGraphLoading(false); // ⭐ 통신 및 그리기가 끝나면 로딩 오프 (성공/실패 무관)
            // }
        };

        // 💡 탭 전환 후 차트 DOM이 완전히 활성화될 수 있도록 50ms 유예를 둡니다.
        const timer = setTimeout(() => {
            // 복귀 시 멈춤 현상(isSameGraphData 걸림) 방지를 위해 기존 캐시를 한 번 비워줍니다.
            if (uiReady.vibration && vibrationGraphDataRef.current === null) {
                // if (graphConfigs.vibrationAvg.prevDataRef) graphConfigs.vibrationAvg.prevDataRef.current = null;
                // if (graphConfigs.vibrationBox.prevDataRef) graphConfigs.vibrationBox.prevDataRef.current = null;
                // if (graphConfigs.noise.prevDataRef) graphConfigs.noise.prevDataRef.current = null;

                // ⭐ [이 줄 추가!] 실제 캐시를 담고 있는 컴포넌트의 직접적인 Ref들도 확실하게 완전히 초기화해 줍니다.
                vibrationGraphDataRef.current = null;
                prevNoiseDataRef.current = null;
            }

            fetchVibrationOrNoise(true); // 복귀 즉시 그리기 실행!
            startPolling(vibrationPollingRef, () => fetchVibrationOrNoise(false), DELAY_TIME);
        }, 100);

        return () => {
            clearTimeout(timer);
            stopPolling(vibrationPollingRef);
        };
    }, [value, isVisible, sensorPosition?.id, vibrationChartType]);


    // ==========================================
    // 3. 온도 실시간 Polling 및 렌더링 제어
    // ==========================================
    useEffect(() => {
        // 사용자가 온도 탭을 보고 있을 때만 온도 Polling이 작동하도록 제어
        if (!sensorPosition || !isVisible || value !== TAB.TEMPERATURE ) {
            stopPolling(tempPollingRef);
            return;
        }

        const graphParam = { "sensorPosition.id": sensorPosition.id };
        const fetchTemperature = (isFirstRun = false) => {
            // setTempLoading(true); // ⭐ 그리기 시작할 때 로딩 온
            // try {
                const tempChart = tempChartRef.current?.getEchartsInstance();
                if (!tempChart) return;

                return fetchGraph({
                    chart: tempChart,
                    graphParam,
                    config : graphConfigs.temperature,
                    drawChart: true, // 탭 활성화 시 즉시 강제 렌더링
                    forceDraw: isFirstRun,
                    isPolling: !isFirstRun
                });
            // } finally {
            //     setTempLoading(false); // ⭐ 완료 시 로딩 오프
            // }
        };

        const timer = setTimeout(() => {
            // 복귀 시 멈춤 현상 방지를 위해 기존 온도 캐시 비우기
            if (uiReady.temperature && prevTempDataRef.current === null) {
                // if (graphConfigs.temperature.prevDataRef) graphConfigs.temperature.prevDataRef.current = null;
                // ⭐ [이 줄 추가!] 온도 직접 Ref도 확실하게 초기화
                prevTempDataRef.current = null;
            }

            fetchTemperature(true); // 탭 누르자마자 60초 대기 없이 즉시 호출!
            startPolling(tempPollingRef, () => fetchTemperature(false), DELAY_TIME);
        }, 100);

        return () => {
            clearTimeout(timer);
            stopPolling(tempPollingRef);
        };
    }, [value, sensorPosition?.id, isVisible]);

    useEffect(() => {
        // 1. 데이터 비교 캐시 비우기
        vibrationGraphDataRef.current = null;

        const chart = chartRef.current?.getEchartsInstance();
        if (chart) {
            // 2. setTimeout을 사용하여 메인 렌더링 루프를 방해하지 않고 비동기적으로 실행
            setTimeout(() => {
                try {
                    // chart.clear(); // 기존 잔상 제거
                    chart.setOption(chartDataInit(), true); // 초기 빈 옵션 적용 (notMerge: true)
                } catch (error) {
                    console.warn("Chart reset failed safely:", error);
                }
            }, 0); // 0ms 딜레이를 주어 태스크 큐의 가장 앞으로 보냅니다.
        }
    }, [vibrationChartType]);
    
    useEffect(() => {
        if (!sensorPosition || !equipmentPartId) return;

        setIsLoading(false);
        setFile(null);

        getDownloadDirectByQuery({
            fileGroup: "LER",
            id: `${equipmentPartId}_${sensorPosition.sensorType}_${sensorPosition.positionNoBySensorType}`,
        })
        .then(blob => {
            setFile(blob);
            setIsLoading(true);
        })
        .catch(() => {
            setFile(null);
            setIsLoading(false);
        });
    }, [value, sensorPosition]);

    useEffect(()=>{
        if (isLoading && file) {
          handleParse();
        }
    },[isLoading, file]);

    const handleParse = () => {
        const reader = new FileReader();

        reader.onload = async ({ target }) => {
            const csvText = target.result;
            const nextHash = getCsvHash(csvText);

            // ⭐ CSV 변경 없으면 종료
            if (prevCsvHashRef.current === nextHash) {
                return;
            }
            prevCsvHashRef.current = nextHash;
            
            const csv = Papa.parse(csvText, { header: true });
            const parsedData = csv?.data;
            if (!parsedData || parsedData.length === 0) return;

            const columns = parsedData?.[0] ? Object.keys(parsedData[0]) : [];
            if (columns.length === 0) return;

            const datas = parsedData[0];
            setRemainLife(parseInt(datas[columns[3]]));

            let lifeSpans = [];
            let remainLifeSpans = [];

            const validRows = parsedData.filter(r => r[columns[0]]);
            validRows.forEach(row => {
                lifeSpans.push(Math.round(row[columns[1]] * 100) / 100 || 0);
                remainLifeSpans.push(Math.round(row[columns[2]] * 100) / 100 || 0);
            });
            
            setCsvOptions(prev => ({
                ...prev,
                xAxis: {
                    ...prev.xAxis,
                    data: validRows.map(r => r[columns[0]]),
                },
                series: [
                    {
                        name: t('message.common.text.lifespan.index'),
                        type: 'line',
                        areaStyle : {},
                        data: lifeSpans,
                    },
                    {
                        name: t('message.common.text.expect.lifespan.index'),
                        type: 'line',
                        areaStyle : {},
                        data: remainLifeSpans,
                    },
                ],
                legend: {
                    ...prev.legend,
                    data: [
                        t('message.common.text.lifespan.index'),
                        t('message.common.text.expect.lifespan.index'),
                    ],
                },
            }));

            if (!uiReady.csv) {
                setUiReady(prev => ({ ...prev, csv: true }));
            }
        
          // setLifeSpanGraphDatas(chartDataCopy);
        };
        reader.readAsText(file);
    };

    const applyTheme = (chartRef) => {
        const chart = chartRef.current?.getEchartsInstance();
        if (!chart) return;

        chart.setOption(
            {
                textStyle: { color: textColor },
                legend: {
                    backgroundColor: bgColor,
                    textStyle: { color: textColor },
                },
            },
            false,
            true
        );
    };

    useEffect(() => {
        applyTheme(chartRef);
        applyTheme(tempChartRef);
    }, [mode]);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const showGraph =
        value === TAB.GRAPH && uiReady.vibration;

    const showTemperature =
        value === TAB.TEMPERATURE && uiReady.temperature;

    const showCsv =
        value === TAB.CSV &&
        uiReady.csv &&
        isLoading &&
        csvOptions &&
        remainLife !== 0;
        
    if (uiReady.vibration || uiReady.temperature || (isLoading && csvOptions !== null && remainLife !== 0)) {
        let className = '';
        if (type === 'popup') className='pop_tr';
    return (
        <>
            <TableContainer>
                <Table sx={{ minWidth: 500 }} aria-label="simple table" className="same_width">
                    <TableBody>
                        {equipment.iotInstlYn && showGraph &&
                        <TableRow className={className}>
                            <TableCell className="th" align="center">{t('message.common.select.sensor')}</TableCell>
                            <TableCell align="center">
                                {getEnumValueByEnumNameAndCode("SensorType", sensorPosition.sensorType)?.[0]?.title} ({sensorPosition.positionNoBySensorType})
                                {(sensorPosition.sensor?.batLevel === 0 || sensorPosition.sensor?.batLevel) && <img className="bat" style={{margin:"-1.2px 0 0 5px", width:"25px"}} src={`${process.env.PUBLIC_URL}/img/icon/${getBatLevelImgByBatLevel(sensorPosition.sensor?.batLevel)}`} alt={sensorPosition.sensor?.batLevel} title={sensorPosition.sensor?.batLevel} />}
                            </TableCell>
                            <TableCell className="th" align="center">{t('message.common.subtitle.diagnosis.value')}</TableCell>
                            <TableCell align="center" colSpan={3}>
                                {showGraph && sensorPosition.diagnosisResultVibration &&
                                <div style={{display:"flex",alignItems:"center",justifyContent:"center"}}>
                                    {t('message.common.subtitle.axis')} : <div align="center"><span>{sensorPosition.diagnosisResultVibration.overall_shaft.toFixed(2)}</span></div>
                                    &nbsp;&nbsp;{t('message.common.subtitle.radius')} : <div align="center"><span>{sensorPosition.diagnosisResultVibration.overall_horizon.toFixed(2)}</span></div>
                                    &nbsp;&nbsp;{t('message.common.subtitle.vertical')} : <div align="center"><span>{sensorPosition.diagnosisResultVibration.overall_vertical.toFixed(2)}</span></div>
                                </div>
                                }
                                {showGraph && sensorPosition.diagnosisResultNoise &&
                                <div style={{display:"flex",alignItems:"center",justifyContent:"center"}}>
                                    RMS : <div align="center"><span>{sensorPosition.diagnosisResultNoise.rms.toFixed(2)}</span></div>
                                    &nbsp;&nbsp;{t('message.common.subtitle.skewness')} : <div align="center"><span>{sensorPosition.diagnosisResultNoise.skew.toFixed(2)}</span></div>
                                    &nbsp;&nbsp;{t('message.common.subtitle.kurtosis')} : <div align="center"><span>{sensorPosition.diagnosisResultNoise.kurtosis.toFixed(2)}</span></div>
                                </div>
                                }
                            </TableCell>
                        </TableRow>
                        }
                        {equipment.iotInstlYn && showGraph && sensorPosition.diagnosisResultVibration &&
                        <TableRow className={className}>
                            <TableCell className="th" align="center">{t('message.common.subtitle.diagnosis.date')}</TableCell>
                            <TableCell align="center">
                                {fromServerFormat(sensorPosition.diagnosisResultVibration.analysis_at,{format:t('message.common.text.date.format.date')})}
                            </TableCell>
                            <TableCell className="th" align="center">{t('message.common.subtitle.diagnosis.result')}</TableCell>
                            <TableCell align="center" colSpan={3}>
                                <div style={{display:"flex",alignItems:"center",justifyContent:"center"}}>
                                    {t('message.common.subtitle.axis')} : <div align="center" className={getDiagnosisGradeColor(sensorPosition.diagnosisResultVibration.rank_shaft)}><span>{sensorPosition.diagnosisResultVibration.rank_shaft}</span></div>
                                    {t('message.common.subtitle.radius')} : <div align="center" className={getDiagnosisGradeColor(sensorPosition.diagnosisResultVibration.rank_horizon)}><span>{sensorPosition.diagnosisResultVibration.rank_horizon}</span></div>
                                    {t('message.common.subtitle.vertical')} : <div align="center" className={getDiagnosisGradeColor(sensorPosition.diagnosisResultVibration.rank_vertical)}><span>{sensorPosition.diagnosisResultVibration.rank_vertical}</span></div>
                                </div>
                            </TableCell>
                        </TableRow>
                        }
                        {equipment.iotInstlYn && showGraph && sensorPosition.diagnosisResultNoise &&
                        <TableRow className={className}>
                            <TableCell className="th" align="center">{t('message.common.subtitle.diagnosis.date')}</TableCell>
                            <TableCell align="center">
                                {fromServerFormat(sensorPosition.diagnosisResultNoise.analysis_at,{format:t('message.common.text.date.format.date')})}
                            </TableCell>
                            <TableCell className="th" align="center"></TableCell>
                            <TableCell align="center" colSpan={3}></TableCell>
                        </TableRow>
                        }

                        {equipment.iotInstlYn && showTemperature && sensorPosition.diagnosisResultVibration &&
                        <TableRow className={className}>
                            <TableCell className="th" align="center">{t('message.common.select.sensor')}</TableCell>
                            <TableCell align="center">{getEnumValueByEnumNameAndCode("SensorType", sensorPosition.sensorType)?.[0]?.title} ({sensorPosition.positionNoBySensorType})</TableCell>
                            <TableCell className="th" align="center">{t('message.common.subtitle.diagnosis.value')}</TableCell>
                            <TableCell align="center" colSpan={3}>
                                <div style={{display:"flex",alignItems:"center",justifyContent:"center"}}>
                                    {sensorPosition.equipmentTemperature &&
                                    <>&nbsp;&nbsp;{t('message.common.subtitle.temperature')} : <div align="center"><span>{sensorPosition.equipmentTemperature.temperature.toFixed(2)}</span></div></>
                                    }
                                </div>
                            </TableCell>
                        </TableRow>
                        }
                    </TableBody>
                </Table>
            </TableContainer>

            <div className="row">
                <div className="mixed-chart">
                    <Box>
                        <Tabs value={value} onChange={handleChange} style={{paddingTop:"10px", paddingLeft:"10px"}} sx={{ ".Mui-selected":{bgcolor:"#0a2e60"}, ".MuiTabs-flexContainer":{flexWrap:"wrap"} }}>
                            {uiReady.vibration && 
                            <Tab value={TAB.GRAPH}
                                component="div"
                                className="graphTab"
                                style={{border:`${value === TAB.GRAPH ? "3px solid #0061d0": "1px solid #0061d0"}`}}
                                label={
                                    <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                                        {(sensorPosition && sensorPosition.sensorType === 'V' ? t('message.common.chip.vibration.trend.graph') : t('message.common.chip.grade.history.trend.graph'))+" ("+equipmentPartName+")"}
                                        &nbsp;&nbsp;
                                        <EquipmentPartSensorsGraphsLayer equipment={equipment} sensorPosition={sensorPosition} equipmentPartId={equipmentPartId} 
                                            equipmentPartPositionName={equipmentPartPositionName} equipmentPartName={equipmentPartName} vibrationChartType={vibrationChartType} type="SV" />
                                    </Box>
                                }
                            />
                            }
                            {hasTempData && 
                            <Tab value={TAB.TEMPERATURE}
                                component="div"
                                className="graphTab" 
                                style={{border:`${value === TAB.TEMPERATURE ? "3px solid #0061d0": "1px solid #0061d0"}`}}
                                label={
                                <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                                {t('message.common.chip.temperature.trend.graph')}
                                &nbsp;&nbsp;
                                <EquipmentPartSensorsGraphsLayer equipment={equipment} sensorPosition={sensorPosition} equipmentPartId={equipmentPartId} 
                                    equipmentPartPositionName={equipmentPartPositionName} equipmentPartName={equipmentPartName} type="ST" />
                                </Box>
                                } />
                            }
                            {isLoading && csvOptions !== null && remainLife !== 0 &&
                            <Tab value={TAB.CSV} 
                                label={`${t('message.common.chip.expect.lifespan')} (${t('message.common.chip.remain.lifespan')}:${remainLife}${t('message.common.text.month')})`} 
                                className="graphTab"
                                style={{height:'35px', border:`${value === TAB.CSV ? "3px solid #0061d0": "1px solid #0061d0"}`}}
                            />
                            }
                        </Tabs>

                        {hasBoxData && value === TAB.GRAPH && sensorPosition?.sensorType === 'V' && (
                        <div style={{width:"100%", display:"flex", justifyContent:"center"}}>
                            <ToggleButtonGroup
                                value={vibrationChartType}
                                exclusive
                                onChange={(event, newValue) => {
                                    if (newValue !== null) {
                                        setVibrationChartType(newValue);
                                    }
                                }}
                                sx={{
                                    marginTop:"10px",
                                    '& .Mui-selected': {
                                        // backgroundColor: '#0061d0 !important',
                                        color: '#fff !important',
                                        borderRadius:"10px",
                                        border: "3px solid #00c2d0"
                                    }
                                }}
                            >
                                <ToggleButton value="AVG">
                                    {t('message.common.subtitle.avg.trend')}
                                </ToggleButton>

                                <ToggleButton value="BOX">
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        {t('message.common.subtitle.box.plot')}

                                        {sensorPosition?.sensorType === 'V' &&
                                            vibrationChartType === "BOX" && (
                                            <Tooltip
                                                arrow
                                                placement="bottom"
                                                slotProps={{
                                                    tooltip: {
                                                        sx: {
                                                            bgcolor: '#2b2b2b',
                                                            color: '#fff',
                                                            fontSize: 13,
                                                            maxWidth: 260,
                                                            border: '1px solid #666'
                                                        }
                                                    },
                                                    arrow: {
                                                        sx: {
                                                            color: '#2b2b2b'
                                                        }
                                                    }
                                                }}
                                                title={
                                                    <>
                                                        <Box fontWeight={600} mb={1}>
                                                            {t('message.common.boxplot.title')}
                                                        </Box>

                                                        <div><b>Min</b> : {t('message.common.boxplot.min')}</div>
                                                        <div><b>Q1</b> : {t('message.common.boxplot.q1')}</div>
                                                        <div><b>Med</b> : {t('message.common.boxplot.median')}</div>
                                                        <div><b>Q3</b> : {t('message.common.boxplot.q3')}</div>
                                                        <div><b>Max</b> : {t('message.common.boxplot.max')}</div>
                                                    </>
                                                }
                                            >
                                                <InfoOutlinedIcon
                                                    fontSize="small"
                                                    color="info"
                                                    sx={{ ml: .5, cursor: 'pointer' }}
                                                />
                                            </Tooltip>
                                        )}
                                    </Box>
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </div>
                            )
                        }
                        <Box mt={2}>
                            {/* {showGraph &&
                                <MainCard content={false}>
                                    <ECharts
                                      ref={chartRef}
                                      option={vibrationBaseOptions.current}
                                      opts={{ renderer: 'svg', height: '300px' }}
                                      />
                                </MainCard>
                            }
                            {showTemperature &&
                                <MainCard content={false}>
                                    <ECharts
                                    ref={tempChartRef}
                                    option={tempBaseOptions.current}
                                    opts={{ renderer: 'svg', height: '300px' }}
                                    />
                                </MainCard>
                            } */}
                            {showGraph && (
                                <MainCard 
                                    content={false} 
                                    style={{ 
                                        display: uiReady.vibration ? 'block' : 'none',
                                        position: 'relative' // ⭐ 로딩 레이아웃의 기준점 설정
                                    }}
                                >
                                    {/* ⭐ 진동 차트 로딩 딤처리 오버레이 */}
                                    {graphLoading && (
                                        <Box 
                                            display="flex" 
                                            alignItems="center" 
                                            justifyContent="center" 
                                            sx={{
                                                position: 'absolute',
                                                top: 0, left: 0, right: 0, bottom: 0,
                                                backgroundColor: 'rgba(47, 47, 47, 0.75)', // 현재 다크테마 bgColor와 연동
                                                zIndex: 10,
                                                borderRadius: '4px'
                                            }}
                                        >
                                            <CircularProgress color="primary" />
                                        </Box>
                                    )}
                                    <ECharts
                                        ref={chartRef}
                                        option={vibrationBaseOptions.current}
                                        opts={{ renderer: 'svg', height: '300px' }}
                                        onChartReady={(chart) => {
                                            if (sensorPosition?.sensorType === 'V' && vibrationGraphDataRef.current) {
                                                updateVibrationChart(chart, vibrationGraphDataRef.current, t, textColor, bgColor, vibrationChartType);
                                            }
                                            if (sensorPosition?.sensorType === 'N' && prevNoiseDataRef.current) {
                                                updateNoiseChart(chart, prevNoiseDataRef.current, t, textColor, bgColor);
                                            }
                                        }}
                                    />
                                </MainCard>
                            )}
                            {/* 💡 온도 차트도 동일한 문제가 발생할 수 있으므로 함께 수정합니다. */}
                            {showTemperature && (
                                <MainCard 
                                    content={false} 
                                    style={{ 
                                        display: uiReady.temperature ? 'block' : 'none',
                                        position: 'relative' // ⭐ 로딩 레이아웃의 기준점 설정
                                    }}
                                >
                                    {/* ⭐ 온도 차트 로딩 딤처리 오버레이 */}
                                    {tempLoading && (
                                        <Box 
                                            display="flex" 
                                            alignItems="center" 
                                            justifyContent="center" 
                                            sx={{
                                                position: 'absolute',
                                                top: 0, left: 0, right: 0, bottom: 0,
                                                backgroundColor: 'rgba(47, 47, 47, 0.75)',
                                                zIndex: 10,
                                                borderRadius: '4px'
                                            }}
                                        >
                                            <CircularProgress color="primary" />
                                        </Box>
                                    )}
                                    <ECharts
                                        ref={tempChartRef}
                                        option={tempBaseOptions.current}
                                        opts={{ renderer: 'svg', height: '300px' }}
                                        onChartReady={(tempChart) => {
                                            if (prevTempDataRef.current) {
                                                updateTempChart(tempChart, prevTempDataRef.current, t, textColor, bgColor);
                                            }
                                        }}
                                    />
                                </MainCard>
                            )}
                            {showCsv &&
                                <CSVReaderGraphTab options={csvOptions} remainLife={remainLife} />
                            }
                        </Box>
                    </Box>
                </div>
            </div>
            <div className="divide" />
        </>
    )
   }
}

export default EquipmentPartSensorsGraphsWithBox;